import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Form, Input, Select, Button, message, Typography, Spin, Space, Upload, Badge, InputNumber, Row, Col } from "antd";
import { SaveOutlined, ReloadOutlined, ArrowLeftOutlined, UploadOutlined, SortAscendingOutlined, TranslationOutlined, SettingOutlined, EyeOutlined, PictureOutlined } from "@ant-design/icons";
import videoService from "../../../services/videoService";
import masterService from "../../../services/masterService";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

const EditLanguage = () => {
  const { lang_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [imageUrl, setImageUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  const fetchLanguageDetails = useCallback(async () => {
    if (!lang_id) {
      message.error("Language ID is required");
      navigate("/languages");
      return;
    }

    setLoadingData(true);
    try {
      const response = await videoService.getSingleVideoLanguage(lang_id);

      if (response.data && response.data.status) {
        const languageData = response.data.data[0] || response.data.data;
        if (languageData && languageData.lang_name) {
          console.log("languageData", languageData);
          form.setFieldsValue({
            lang_name: languageData.lang_name,
            lang_iso_639_2_code: languageData.lang_iso_639_2_code,
            native_name: languageData.native_name,
            lang_is_active: languageData.lang_is_active || "Y",
            is_translation_active: languageData.is_translation_active || "Y",
            show_in_preference: languageData.show_in_preference || "N",
            preference_order_number: languageData.preference_order_number || 0,
            image_url: languageData.image_url,
            logo_url: languageData.logo_url,
            lang_description: languageData.lang_description || "",
            lang_keywords: languageData.lang_keywords
              ? String(languageData.lang_keywords)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
          });
          setImageUrl(languageData.image_url || "");
          setLogoUrl(languageData.logo_url || "");
        } else {
          message.error("Language data not found");
          navigate("/languages");
        }
      } else {
        message.error(
          response.data?.message || "Failed to fetch language details"
        );
        navigate("/languages");
      }
    } catch (error) {
      console.error("Error fetching language details:", error);
      message.error("Error fetching language details");
      navigate("/languages");
    } finally {
      setLoadingData(false);
    }
  }, [lang_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Language");
    fetchLanguageDetails();
  }, [fetchLanguageDetails, setPageTitle]);

  const onImageUpload = async (file, key) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "language");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      console.log(
        "response.data.data.s3_key",
        response.data.data.s3_key
      );
      const uploadedUrl = response.data.data.s3_key;
      form.setFieldValue(key, uploadedUrl);

      // Update state variables to trigger re-render
      if (key === "image_url") {
        setImageUrl(uploadedUrl);
      } else if (key === "logo_url") {
        setLogoUrl(uploadedUrl);
      }

      message.success("Image uploaded successfully");
    } catch (e) {
      message.error("Failed to upload image");
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const langKeywords = Array.isArray(values.lang_keywords)
        ? values.lang_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
        : values.lang_keywords || "";
      const payload = {
        ...values,
        lang_id: lang_id,
        lang_keywords: langKeywords,
      };

      const response = await videoService.editVideoLanguage(lang_id, payload);

      if (response.data && response.data.status) {
        message.success(
          response.data.message || "Language updated successfully"
        );
        navigate("/languages");
      } else {
        message.error(response.data.message || "Failed to update language");
      }
    } catch (error) {
      console.error("Error updating language:", error);
      message.error("Error updating language");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/languages");
  };

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header Section */}

        <Spin spinning={loadingData}>
          <Card
          >
            <Form
              form={form}
              layout="vertical"
              size="large"
              onFinish={handleSubmit}
              className="space-y-6"
            >
              {/* Basic Information Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <TranslationOutlined  />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Basic Information
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language Name <span className="text-red-500">*</span>
                        </span>
                      }
                      name="lang_name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter language name",
                        },
                      ]}
                      
                    >
                      <Input
                        placeholder="Enter language name"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language ISO Code
                        </span>
                      }
                      name="lang_iso_639_2_code"
                      
                    >
                      <Input
                        placeholder="Enter language ISO code"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Native Name
                        </span>
                      }
                      name="native_name"
                      
                    >
                      <Input
                        placeholder="Enter native name"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Description
                        </span>
                      }
                      name="lang_description"
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Enter description"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Keywords
                        </span>
                      }
                      name="lang_keywords"
                    >
                      <Select
                        mode="tags"
                        placeholder="Type a keyword and press Enter"
                        tokenSeparators={[","]}
                        open={false}
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Status & Translation Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <SettingOutlined  />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Status & Translation
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Status
                        </span>
                      }
                      name="lang_is_active"
                      
                    >
                      <Select
                        placeholder="Select status"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Active" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="Inactive" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Allow Translation
                        </span>
                      }
                      name="is_translation_active"
                      
                    >
                      <Select
                        placeholder="Select translation option"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Yes" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="No" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Preference Settings Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <EyeOutlined  />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Preference Settings
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Show in Preference
                        </span>
                      }
                      name="show_in_preference"
                      
                    >
                      <Select
                        placeholder="Select preference visibility"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Yes" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="No" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Preference Order
                        </span>
                      }
                      name="preference_order_number"
                      rules={[
                        {
                          pattern: /^(0|[1-9]\d*)$/,
                          message:
                            "Preference order number must be a non-negative integer",
                        },
                      ]}
                      
                    >
                      <InputNumber
                        placeholder="Enter preference order number"
                        style={{ width: "100%" }}
                        className="rounded-lg"
                        addonBefore={<SortAscendingOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Media Assets Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <PictureOutlined  />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Media Assets
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="image_url"
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language Image
                        </span>
                      }
                      
                    >
                      {imageUrl && (
                        <div className="mb-4">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                            <Text strong className="text-orange-700 text-sm">
                              {getImageUrl(imageUrl)}
                            </Text>
                          </div>
                          <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                            <img
                              src={getImageUrl(imageUrl)}
                              alt="Language preview"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(f) => {
                          onImageUpload(f, "image_url");
                          return false;
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className="w-full rounded-lg"
                          type={imageUrl ? "default" : "primary"}
                        >
                          {imageUrl
                            ? "Change Language Image"
                            : "Upload Language Image"}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="logo_url"
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language Logo
                        </span>
                      }
                      
                    >
                      {logoUrl && (
                        <div className="mb-4">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                            <Text strong className="text-orange-700 text-sm">
                              {getImageUrl(logoUrl)}
                            </Text>
                          </div>
                          <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                            <img
                              src={getImageUrl(logoUrl)}
                              alt="Logo preview"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(f) => {
                          onImageUpload(f, "logo_url");
                          return false;
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className="w-full rounded-lg"
                          type={logoUrl ? "default" : "primary"}
                        >
                          {logoUrl
                            ? "Change Language Logo"
                            : "Upload Language Logo"}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Action Buttons */}
              <div
                className="flex justify-end gap-4 pt-6 border-t"
                style={{ borderColor: "#d9d9d9" }}
              >
                <Space size="middle">
                  <Button
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleGoBack}
                    icon={<ArrowLeftOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    className="px-8"
                  >
                    {loading ? "Updating..." : "Update Language"}
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default EditLanguage;
